import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sidebar from '../components/Sidebar';
import { getSidebarItemsForRole } from '../components/SidebarPresets';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/apiconfig';

// Section component for reusable card-like sections
function Section({ title, children, right = null }) {
  return (
    <section className="bg-white rounded-xl shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {right && <div>{right}</div>}
      </div>
      {children}
    </section>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  right: PropTypes.node,
};

export default function ClusterAdminDashboard() {
  const { user } = useAuth();
  const sidebar = useSidebar();
  const location = useLocation();
  const companyParam = user?.companyKey || user?.companyId;
  const clusterParam = user?.clusterKey || user?.clusterId;
  const [activeTab, setActiveTab] = useState('details');

  const [jobPosts, setJobPosts] = useState([]);
  const [jobPostsLoading, setJobPostsLoading] = useState(false);
  const [jobPostsError, setJobPostsError] = useState('');

  const [jobPostModalOpen, setJobPostModalOpen] = useState(false);
  const [selectedJobPost, setSelectedJobPost] = useState(null);

  const [recruiterModalOpen, setRecruiterModalOpen] = useState(false);
  const [recruiterModalLoading, setRecruiterModalLoading] = useState(false);
  const [recruiterModalError, setRecruiterModalError] = useState('');
  const [recruiterProfile, setRecruiterProfile] = useState(null);
  const [tab, setTab] = useState('approved'); // Recruiters tab state
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cluster, setCluster] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [company, setCompany] = useState(null);
  const [clusterAdmins, setClusterAdmins] = useState([]);
  const [unitAdmins, setUnitAdmins] = useState([]);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);

  // Fetch initial data (tree, cluster, company, admins)
  useEffect(() => {
    if (!companyParam || !clusterParam) {
      setError('Missing company or cluster parameters');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch company tree
        const tRes = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/tree`);
        if (!tRes.ok) throw new Error(`Failed to load tree: ${tRes.statusText}`);
        const treeData = await tRes.json();
        setTree(treeData);

        // Fetch cluster list and select current cluster
        const cRes = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters`);
        if (!cRes.ok) throw new Error(`Failed to load clusters: ${cRes.statusText}`);
        const clusters = await cRes.json();
        const currentCluster = (clusters || []).find((x) => String(x.id) === String(clusterParam));
        setCluster(currentCluster || { id: clusterParam, name: '' });
        setEditDraft(currentCluster || { id: clusterParam, name: '' });

        // Fetch company details
        const compRes = await fetch(`${API_BASE_URL}/api/companies/${companyParam}`);
        if (!compRes.ok) throw new Error(`Failed to load company: ${compRes.statusText}`);
        setCompany(await compRes.json());

        // Fetch admins
        const [clA, uA] = await Promise.all([
          fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters/${clusterParam}/admins`).then((r) => {
            if (!r.ok) throw new Error('Failed to load cluster admins');
            return r.json();
          }),
          fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters/${clusterParam}/unit-admins`).then((r) => {
            if (!r.ok) throw new Error('Failed to load unit admins');
            return r.json();
          }),
        ]);
        setClusterAdmins(Array.isArray(clA) ? clA : []);
        setUnitAdmins(Array.isArray(uA) ? uA : []);
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyParam, clusterParam]);

  // Fetch recruiters for pending, approved, and rejected tabs
  async function loadRecruiters() {
    if (!companyParam || !clusterParam) return;
    try {
      setError('');
      const [p, a, rj] = await Promise.all([
        fetch(
          `${API_BASE_URL}/api/companies/${companyParam}/recruiters?scope=cluster&clusterId=${clusterParam}&status=pending`
        ).then((r) => {
          if (!r.ok) throw new Error('Failed to load pending recruiters');
          return r.json();
        }),
        fetch(
          `${API_BASE_URL}/api/companies/${companyParam}/recruiters?scope=cluster&clusterId=${clusterParam}&status=approved`
        ).then((r) => {
          if (!r.ok) throw new Error('Failed to load approved recruiters');
          return r.json();
        }),
        fetch(
          `${API_BASE_URL}/api/companies/${companyParam}/recruiters?scope=cluster&clusterId=${clusterParam}&status=rejected`
        ).then((r) => {
          if (!r.ok) throw new Error('Failed to load rejected recruiters');
          return r.json();
        }),
      ]);
      setPending(Array.isArray(p) ? p : []);
      setApproved(Array.isArray(a) ? a : []);
      setRejected(Array.isArray(rj) ? rj : []);
    } catch (e) {
      setError(e.message || 'Failed to load recruiters');
    }
  }

  function openJobPostModal(jp) {
    if (!jp) return;
    setSelectedJobPost(jp);
    setJobPostModalOpen(true);
  }

  useEffect(() => {
    loadRecruiters();
  }, [companyParam, clusterParam]);

  async function loadJobPosts() {
    if (!companyParam || !clusterParam) return;
    setJobPostsLoading(true);
    setJobPostsError('');
    try {
      const qs = new URLSearchParams({ scope: 'cluster', companyId: String(companyParam), clusterId: String(clusterParam) });
      const res = await fetch(`${API_BASE_URL}/api/job-postings/admin?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to load job posts');
      const data = await res.json();
      setJobPosts(Array.isArray(data) ? data : []);
    } catch (e) {
      setJobPostsError(e.message || 'Failed to load job posts');
      setJobPosts([]);
    } finally {
      setJobPostsLoading(false);
    }
  }

  async function deleteJobPost(id) {
    if (!companyParam || !clusterParam || !id) return;
    const ok = window.confirm('Delete this job post?');
    if (!ok) return;
    try {
      const qs = new URLSearchParams({ scope: 'cluster', companyId: String(companyParam), clusterId: String(clusterParam) });
      const res = await fetch(`${API_BASE_URL}/api/job-postings/${id}?${qs.toString()}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await loadJobPosts();
    } catch (e) {
      setJobPostsError(e.message || 'Delete failed');
    }
  }

  async function openRecruiterModal(r) {
    const userId = r?.user_id;
    if (!userId) return;
    setRecruiterModalOpen(true);
    setRecruiterModalLoading(true);
    setRecruiterModalError('');
    setRecruiterProfile(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/recruiter/profile/${encodeURIComponent(String(userId))}`);
      if (!res.ok) throw new Error('Failed to load recruiter details');
      const data = await res.json();
      setRecruiterProfile(data);
    } catch (e) {
      setRecruiterModalError(e.message || 'Failed to load recruiter details');
    } finally {
      setRecruiterModalLoading(false);
    }
  }

  // Keep activeTab in sync with URL hash (React Router updates may not trigger native hashchange)
  useEffect(() => {
    const allowed = new Set((getSidebarItemsForRole(user?.role) || []).map(i => i.id));
    const h = (location.hash || '').replace('#', '');
    if (h && allowed.has(h)) setActiveTab(h);
  }, [user?.role, location.hash]);

  // Default Recruiters view to Approved whenever entering Recruiters tab
  useEffect(() => {
    if (activeTab === 'recruiters') setTab('approved');
  }, [activeTab]);

  // Auto-load Job Posts when entering Job Posts tab
  useEffect(() => {
    if (activeTab === 'job-posts') loadJobPosts();
  }, [activeTab, companyParam, clusterParam]);

  // Save cluster details
  async function saveCluster() {
    if (!companyParam || !clusterParam || !editDraft) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters/${clusterParam}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editDraft.name || '' }),
      });
      if (!res.ok) throw new Error(`Failed to save cluster: ${res.statusText}`);
      setCluster(editDraft);
      setEditOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to save cluster');
    } finally {
      setSaving(false);
    }
  }

  // Approve a recruiter
  async function approveRecruiter(id) {
    try {
      const res = await fetch(`${API_BASE_URL}/api/recruiters/${id}/approve`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to approve recruiter');
      await loadRecruiters();
    } catch (e) {
      setError(e.message || 'Failed to approve recruiter');
    }
  }

  // Reject a recruiter
  async function rejectRecruiter(id) {
    try {
      const res = await fetch(`${API_BASE_URL}/api/recruiters/${id}/reject`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to reject recruiter');
      await loadRecruiters();
    } catch (e) {
      setError(e.message || 'Failed to reject recruiter');
    }
  }

  // Check for cluster-admin role
  if (!user || user.role !== 'cluster-admin') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 p-4 rounded">
            Cluster admin access required.
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-x-hidden">
      <Header />
      <main className="flex-1 overflow-hidden">
        <div className="flex h-full min-h-0 min-w-0">
          <Sidebar
            items={getSidebarItemsForRole(user?.role)}
            activeId={activeTab}
            onSelect={setActiveTab}
            isOpen={sidebar.isOpen}
            onOverlayClick={sidebar.close}
            onItemClick={sidebar.close}
          />
          <div className={`flex-1 min-w-0 flex flex-col min-h-0 transition-all duration-300 ${sidebar.isOpen ? 'md:ml-64' : ''}`}>
            <div className="flex-1 min-w-0 p-4 md:p-6 overflow-y-auto">
              {error && (
                <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 mb-6">
                  {error}
                </div>
              )}
              <div className="space-y-6">
                {activeTab === 'details' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                      {/* Company Details */}
                      <Section title="Company Details">
                        {loading ? (
                          <div className="text-gray-500">Loading...</div>
                        ) : company ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Name</div>
                              <div className="font-medium">{company.company_name || '-'}</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Industry</div>
                              <div className="font-medium">{company.industry || '-'}</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Website</div>
                              <div className="font-medium">{company.website || '-'}</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Phone</div>
                              <div className="font-medium">{company.phone || '-'}</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Employees</div>
                              <div className="font-medium">{company.employee_count || '-'}</div>
                            </div>
                            <div className="md:col-span-2">
                              <div className="text-sm text-gray-600 mb-1">Address</div>
                              <div className="font-medium">{company.address || '-'}</div>
                            </div>
                            <div className="md:col-span-2">
                              <div className="text-sm text-gray-600 mb-1">Description</div>
                              <div className="font-medium whitespace-pre-line">{company.description || '-'}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-500">No data</div>
                        )}
                      </Section>

                      {/* Cluster Details */}
                      <Section
                        title="Cluster Details"
                        right={
                          cluster && (
                            <button
                              className="px-3 py-2 rounded bg-indigo-600 text-white"
                              onClick={() => {
                                setEditDraft(cluster);
                                setEditOpen(true);
                              }}
                            >
                              Edit Details
                            </button>
                          )
                        }
                      >
                        {loading ? (
                          <div className="text-gray-500">Loading...</div>
                        ) : cluster ? (
                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <div className="text-sm text-gray-600 mb-1">Cluster Name</div>
                              <div className="font-medium">{cluster.name || '-'}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-500">No data</div>
                        )}
                      </Section>

                      {/* Units in Cluster */}
                      <Section title="Units in this Cluster">
                        {loading ? (
                          <div className="text-gray-500">Loading...</div>
                        ) : tree?.clusters?.find((c) => String(c.id) === String(clusterParam))?.units?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full border">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="border px-3 py-2 text-left">Name</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tree.clusters
                                  .find((c) => String(c.id) === String(clusterParam))
                                  ?.units.map((u) => (
                                    <tr key={u.id} className="odd:bg-white even:bg-gray-50">
                                      <td className="border px-3 py-2">{u.name}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-gray-500">No units</div>
                        )}
                      </Section>
                    </div>
                  </div>
                )}

                {activeTab === 'recruiters' && (
                  <div className="grid grid-cols-1 gap-6">
                    <Section title="Recruiters">
                      <div className="mb-6 flex flex-wrap gap-2">
                        <button
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            tab === 'pending'
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          onClick={() => setTab('pending')}
                        >
                          Pending Approvals
                          {pending.length > 0 && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-800 rounded-full">
                              {pending.length}
                            </span>
                          )}
                        </button>
                        <button
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            tab === 'approved'
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          onClick={() => setTab('approved')}
                        >
                          Approved
                        </button>
                        <button
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            tab === 'rejected'
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          onClick={() => setTab('rejected')}
                        >
                          Rejected
                        </button>
                        <button
                          className="ml-auto px-4 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={loadRecruiters}
                        >
                          Refresh
                        </button>
                      </div>

                      {tab === 'pending' ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border px-3 py-2 text-left">Name</th>
                                <th className="border px-3 py-2 text-left">Email</th>
                                <th className="border px-3 py-2 text-left">Phone</th>
                                <th className="border px-3 py-2 text-left">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pending.length > 0 ? (
                                pending.map((r) => (
                                  <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                                    <td className="border px-3 py-2">
                                      <button className="text-indigo-600 hover:underline" onClick={()=>openRecruiterModal(r)}>
                                        {r.first_name || ''} {r.last_name || ''}
                                      </button>
                                    </td>
                                    <td className="border px-3 py-2">{r.work_email || '-'}</td>
                                    <td className="border px-3 py-2">{r.phone || '-'}</td>
                                    <td className="border px-3 py-2">
                                      <button
                                        className="px-2 py-1 text-sm bg-green-600 text-white rounded mr-2"
                                        onClick={() => approveRecruiter(r.id)}
                                      >
                                        Approve
                                      </button>
                                      <button
                                        className="px-2 py-1 text-sm bg-red-600 text-white rounded"
                                        onClick={() => rejectRecruiter(r.id)}
                                      >
                                        Reject
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="border px-3 py-4 text-center text-gray-500" colSpan={4}>
                                    No pending approvals
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : tab === 'approved' ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border px-3 py-2 text-left">Name</th>
                                <th className="border px-3 py-2 text-left">Email</th>
                                <th className="border px-3 py-2 text-left">Phone</th>
                                <th className="border px-3 py-2 text-left">Account Status</th>
                                <th className="border px-3 py-2 text-left">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {approved.length > 0 ? (
                                approved.map((r) => (
                                  <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                                    <td className="border px-3 py-2">
                                      <button className="text-indigo-600 hover:underline" onClick={()=>openRecruiterModal(r)}>
                                        {r.first_name || ''} {r.last_name || ''}
                                      </button>
                                    </td>
                                    <td className="border px-3 py-2">{r.work_email || '-'}</td>
                                    <td className="border px-3 py-2">{r.phone || '-'}</td>
                                    <td className="border px-3 py-2">{r.account_status || 'active'}</td>
                                    <td className="border px-3 py-2">
                                      {r.account_status === 'active' ? (
                                        <button
                                          className="px-2 py-1 text-sm bg-yellow-50 text-yellow-700 rounded"
                                          onClick={async () => {
                                            try {
                                              const res = await fetch(
                                                `${API_BASE_URL}/api/recruiters/${r.id}/deactivate`,
                                                { method: 'POST' }
                                              );
                                              if (!res.ok) throw new Error('Failed to deactivate recruiter');
                                              await loadRecruiters();
                                            } catch (e) {
                                              setError(e.message || 'Failed to deactivate recruiter');
                                            }
                                          }}
                                        >
                                          Set Inactive
                                        </button>
                                      ) : (
                                        <button
                                          className="px-2 py-1 text-sm bg-green-600 text-white rounded"
                                          onClick={async () => {
                                            try {
                                              const res = await fetch(
                                                `${API_BASE_URL}/api/recruiters/${r.id}/activate`,
                                                { method: 'POST' }
                                              );
                                              if (!res.ok) throw new Error('Failed to activate recruiter');
                                              await loadRecruiters();
                                            } catch (e) {
                                              setError(e.message || 'Failed to activate recruiter');
                                            }
                                          }}
                                        >
                                          Set Active
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="border px-3 py-4 text-center text-gray-500" colSpan={5}>
                                    No members
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border px-3 py-2 text-left">Name</th>
                                <th className="border px-3 py-2 text-left">Email</th>
                                <th className="border px-3 py-2 text-left">Phone</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rejected.length > 0 ? (
                                rejected.map((r) => (
                                  <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                                    <td className="border px-3 py-2">
                                      <button className="text-indigo-600 hover:underline" onClick={()=>openRecruiterModal(r)}>
                                        {r.first_name || ''} {r.last_name || ''}
                                      </button>
                                    </td>
                                    <td className="border px-3 py-2">{r.work_email || '-'}</td>
                                    <td className="border px-3 py-2">{r.phone || '-'}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="border px-3 py-4 text-center text-gray-500" colSpan={3}>
                                    No rejected
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Section>
                  </div>
                )}

                {activeTab === 'job-posts' && (
                  <div className="grid grid-cols-1 gap-6">
                    <Section
                      title="Job Posts"
                      right={<button className="px-4 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors" onClick={loadJobPosts}>Refresh</button>}
                    >
                      {jobPostsError && (
                        <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 mb-3">{jobPostsError}</div>
                      )}
                      {jobPostsLoading ? (
                        <div className="text-gray-500">Loading...</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border px-3 py-2 text-left">Title</th>
                                <th className="border px-3 py-2 text-left">Recruiter</th>
                                <th className="border px-3 py-2 text-left">Status</th>
                                <th className="border px-3 py-2 text-left">Posted Date</th>
                                <th className="border px-3 py-2 text-left">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {jobPosts.map(jp => (
                                <tr key={jp.id} className="odd:bg-white even:bg-gray-50">
                                  <td className="border px-3 py-2">
                                    <button className="text-indigo-600 hover:underline" onClick={() => openJobPostModal(jp)}>
                                      {jp.title || '-'}
                                    </button>
                                  </td>
                                  <td className="border px-3 py-2">{jp.recruiter_name || jp.recruiter_email || '-'}</td>
                                  <td className="border px-3 py-2">{jp.status || '-'}</td>
                                  <td className="border px-3 py-2">{jp.post_date ? String(jp.post_date).slice(0, 10) : '-'}</td>
                                  <td className="border px-3 py-2">
                                    <button className="px-2 py-1 text-sm bg-red-600 text-white rounded" onClick={()=>deleteJobPost(jp.id)}>Delete</button>
                                  </td>
                                </tr>
                              ))}
                              {jobPosts.length === 0 && (
                                <tr>
                                  <td className="border px-3 py-4 text-center text-gray-500" colSpan={5}>No job posts</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Section>
                  </div>
                )}

                {activeTab === 'admins' && (
                  <div className="grid grid-cols-1 gap-6">
                    {/* Cluster Admins */}
                    <Section title="Admins - Cluster">
                      {loading ? (
                        <div className="text-gray-500">Loading...</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border px-3 py-2 text-left">Cluster</th>
                                <th className="border px-3 py-2 text-left">Email</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clusterAdmins.length > 0 ? (
                                clusterAdmins.map((a) => (
                                  <tr key={`cladmin-${a.cluster_id}-${a.email}`} className="odd:bg-white even:bg-gray-50">
                                    <td className="border px-3 py-2">{a.cluster_name}</td>
                                    <td className="border px-3 py-2">{a.email}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="border px-3 py-4 text-center text-gray-500" colSpan={2}>
                                    No cluster admins
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Section>

                    {/* Unit Admins */}
                    <Section title="Admins - Units in Cluster">
                      {loading ? (
                        <div className="text-gray-500">Loading...</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border px-3 py-2 text-left">Unit</th>
                                <th className="border px-3 py-2 text-left">Email</th>
                              </tr>
                            </thead>
                            <tbody>
                              {unitAdmins.length > 0 ? (
                                unitAdmins.map((a) => (
                                  <tr key={`uadmin-${a.unit_id}-${a.email}`} className="odd:bg-white even:bg-gray-50">
                                    <td className="border px-3 py-2">{a.unit_name}</td>
                                    <td className="border px-3 py-2">{a.email}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="border px-3 py-4 text-center text-gray-500" colSpan={2}>
                                    No unit admins
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Section>
                  </div>
                )}

                {activeTab === 'units' && (
                  <div className="grid grid-cols-1 gap-6">
                    {/* Units in Cluster */}
                    <Section title="Units in this Cluster">
                      {loading ? (
                        <div className="text-gray-500">Loading...</div>
                      ) : tree?.clusters?.find((c) => String(c.id) === String(clusterParam))?.units?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="border px-3 py-2 text-left">Name</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tree.clusters
                                .find((c) => String(c.id) === String(clusterParam))
                                ?.units.map((u) => (
                                  <tr key={u.id} className="odd:bg-white even:bg-gray-50">
                                    <td className="border px-3 py-2">{u.name}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-gray-500">No units</div>
                      )}
                    </Section>
                  </div>
                )}

                {activeTab === 'structure' && (
                  <div className="grid grid-cols-1 gap-6">
                    {/* Company Structure */}
                    <Section title="Company Structure">
                      {loading ? (
                        <div className="text-gray-500">Loading...</div>
                      ) : tree ? (
                        <div className="space-y-3">
                          <div className="font-medium">{tree.company?.name || 'Unknown Company'}</div>
                          <ul className="pl-4 list-disc">
                            {(tree.clusters || []).map((c) => (
                              <li key={c.id}>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{c.name}</span>
                                  {String(c.id) === String(clusterParam) && (
                                    <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200">
                                      You are Cluster Admin
                                    </span>
                                  )}
                                </div>
                                {c.units?.length > 0 && (
                                  <ul className="pl-5 list-[circle]">
                                    {c.units.map((u) => (
                                      <li key={u.id}>{u.name}</li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="text-gray-500">No data</div>
                      )}
                    </Section>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {jobPostModalOpen && selectedJobPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Job Post Details</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => { setJobPostModalOpen(false); setSelectedJobPost(null); }}>✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <div className="text-sm text-gray-600 mb-1">Title</div>
                <div className="font-medium">{selectedJobPost.title || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Recruiter</div>
                <div className="font-medium">{selectedJobPost.recruiter_name || selectedJobPost.recruiter_email || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Status</div>
                <div className="font-medium">{selectedJobPost.status || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Posted Date</div>
                <div className="font-medium">{selectedJobPost.post_date ? String(selectedJobPost.post_date).slice(0, 10) : '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Location</div>
                <div className="font-medium">{selectedJobPost.location || selectedJobPost.job_location || '-'}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-sm text-gray-600 mb-1">Description</div>
                <div className="font-medium whitespace-pre-line">{selectedJobPost.description || selectedJobPost.job_description || '-'}</div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={() => { setJobPostModalOpen(false); setSelectedJobPost(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Cluster Modal */}
      {editOpen && editDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Cluster Details</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setEditOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1" htmlFor="cluster-name">
                  Cluster Name
                </label>
                <input
                  id="cluster-name"
                  className="border rounded p-2 w-full"
                  value={editDraft.name || ''}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  placeholder="Enter cluster name"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded border"
                onClick={() => setEditOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-50"
                disabled={saving || !editDraft.name.trim()}
                onClick={saveCluster}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}